import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import ContactPickerField from "../risikoanalyseStep/ContactPickerField";
import { asKontaktRef, type KontaktRef } from "./beteiligteTypes";
import { immoText, type Ansprache } from "./texte";
import { TelefonEingabe } from "../forms/widgets/TelefonEingabe";

/**
 * Eine Rolle am Objekt — als **Kontakt** statt als Freitext (Plan §0, E8).
 *
 * Schreibt immer **zwei** Answer-Keys:
 *  - `kontaktKey` → `KontaktRef` (mit `contactId`, sobald ein echter Kontakt
 *    dahintersteht), Vertrag mit dem späteren Persistenz-PR.
 *  - `nameKey` → der Klartext-Name. Den lesen alle Bestands-Downstream-Ansichten
 *    weiter, und er bleibt korrekt, wenn die `contactId` nach einem
 *    Kontakt-Merge ins Leere zeigt.
 *
 * `lookup` entscheidet über den Modus — und ist zugleich die Datenschutz-
 * Sperre. Gebildet wird er in `RisikoImmobilieStepForm` aus
 * `chrome === "embedded" && contactLookup === true`:
 *  - `true` (Backoffice, authentifiziert): `ContactPickerField` — Knopf plus
 *    Refined-Popup (`KontaktAuswahlPopup` → `RelationContactPicker`, dieselbe
 *    Auswahl wie in der Kundenakte) mit Suche im Kontaktbestand, Person/Firma
 *    und Neuanlage im selben Popup.
 *  - `false` (öffentlicher Kundenweg): reine Eingabefelder, **kein**
 *    Netzwerkaufruf gegen `/contact`. Der Kunde hat bewusst keinen Zugriff auf
 *    den Kontaktbestand; das Back-Office legt den Kontakt beim Übernehmen an
 *    (`uebernommenAlsContactId` / `uebernommenAt`, Muster `VerantEntry`).
 *    Regressionsschutz dafür: `KontaktFeldOeffentlich.test.tsx`.
 */

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  hint: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.8rem",
    color: BRAND.muted,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "10px",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  field: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
    minWidth: 0,
  },
  fullSpan: {
    "@media (min-width: 640px)": { gridColumn: "1 / -1" },
  },
  label: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("4px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    transitionProperty: "border-color",
    transitionDuration: "120ms",
    ":focus": { ...shorthands.borderColor(BRAND.purple) },
  },
  blockLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
  },
});

type Props = {
  /** Answer-Key des `KontaktRef`. */
  kontaktKey: string;
  /** Answer-Key des gespiegelten Klartext-Namens. */
  nameKey: string;
  label: string;
  /** Überschrift des Auswahl-Dialogs, z. B. „Mieter auswählen". */
  dialogTitle: string;
  /** Kontaktbestand durchsuchbar? Nur auf dem authentifizierten Weg. */
  lookup: boolean;
  answers: Record<string, unknown>;
  /**
   * Einzelschreiber auf einen Answer-Bag (Schritt-Formular). Schreibt zwei
   * Keys nacheinander — zulässig nur, wenn der Empfänger funktional mergt
   * (`setGebaeudeAnswer` tut das).
   */
  onChange?: (key: string, value: unknown) => void;
  /**
   * Batch-Schreiber: Kontakt **und** Name in EINEM Vorgang. Pflicht überall
   * dort, wo der Empfänger aus einem gerenderten Prop-Wert rechnet — zwei
   * getrennte Aufrufe würden sich dort gegenseitig überschreiben (der zweite
   * Aufruf sieht den ersten nicht und wirft den `kontaktRef` weg).
   * Hat Vorrang vor `onChange`.
   */
  onRefChange?: (ref: KontaktRef) => void;
  /** Hinweistext für den Weg ohne Kontaktbestand ausblenden. */
  hideHint?: boolean;
  ansprache?: Ansprache;
};

const KontaktFeld = ({
  kontaktKey,
  nameKey,
  label,
  dialogTitle,
  lookup,
  answers,
  onChange,
  onRefChange,
  hideHint = false,
  ansprache = "kunde",
}: Props) => {
  const styles = useStyles();
  // Bestand kann unter dem Kontakt-Key noch nichts haben — dann trägt der
  // Name-Key den Wert. Beide Quellen zusammenführen, damit ein Altbestand
  // („Meier GmbH" als Freitext) im Picker nicht als leer erscheint.
  const ref =
    asKontaktRef(answers[kontaktKey]) ?? asKontaktRef(answers[nameKey]);

  const setRef = (next: KontaktRef) => {
    if (onRefChange) {
      onRefChange(next);
      return;
    }
    onChange?.(kontaktKey, next);
    onChange?.(nameKey, next.label);
  };

  if (lookup) {
    return (
      <ContactPickerField
        field={{ key: kontaktKey, label, type: "contactPicker" }}
        value={ref}
        dialogTitle={dialogTitle}
        onChange={(_, v) => {
          // Über den bisherigen Wert spreaden, nicht ersetzen: Telefon und die
          // Übernahme-Stempel stammen typischerweise vom Kundenweg und wären
          // sonst still weg, sobald das Back-Office einen Kontakt auswählt.
          const vorherOhneKontakt = !!ref && ref.contactId == null;
          const hatteInhalt =
            !!ref &&
            (!!ref.label.trim() || !!ref.email?.trim() || !!ref.telefon?.trim());
          // Genau hier findet die Übernahme statt: eine Kundenangabe ohne
          // `contactId` bekommt einen echten Kontakt. Gestempelt wird nach dem
          // Muster von `VerantEntry` (AnalysemodusAuswertungPage).
          const uebernahme =
            vorherOhneKontakt && hatteInhalt && v.contactId != null;
          // Der Bestand wandert NUR mit, solange er zu diesem Datensatz gehört:
          // eine Kundenangabe ohne `contactId` (die gerade angereichert wird)
          // oder derselbe Kontakt noch einmal gewählt. Beim Wechsel auf einen
          // ANDEREN Kontakt erbt dieser nichts — `formatContact` liefert
          // `email: undefined`, sobald ein Kontakt keinen E-Mail-Kanal hat (bei
          // Firmen der Normalfall), und ein `??`-Fallback zöge dann die Adresse
          // des zuvor gewählten Kontakts wieder hoch: im Chip stünde
          // „Schmidt AG" mit der E-Mail von „Meier GmbH", und genau dieser
          // falsche Wert landete unter dem Answer-Key.
          const bestandPasst =
            !ref || vorherOhneKontakt || ref.contactId === v.contactId;
          setRef({
            ...(bestandPasst && ref ? ref : {}),
            contactId: v.contactId,
            label: v.label,
            email: bestandPasst ? (v.email ?? ref?.email) : v.email,
            ...(uebernahme
              ? {
                  uebernommenAlsContactId: String(v.contactId),
                  uebernommenAt: new Date().toISOString(),
                }
              : {}),
          });
        }}
      />
    );
  }

  const patch = (p: Partial<KontaktRef>) =>
    setRef({
      contactId: ref?.contactId ?? null,
      label: ref?.label ?? "",
      email: ref?.email,
      telefon: ref?.telefon,
      uebernommenAlsContactId: ref?.uebernommenAlsContactId,
      uebernommenAt: ref?.uebernommenAt,
      ...p,
    });

  return (
    <div className={styles.root}>
      <span className={styles.blockLabel}>{label}</span>
      {!hideHint && (
        <span className={styles.hint}>
          {immoText("kontaktOhneLookup", ansprache)}
        </span>
      )}
      <div className={styles.grid}>
        <div className={mergeClasses(styles.field, styles.fullSpan)}>
          <span className={styles.label}>Name / Firma</span>
          <input
            className={styles.input}
            value={ref?.label ?? ""}
            onChange={(e) => patch({ label: e.target.value })}
          />
        </div>
        <div className={styles.field}>
          <span className={styles.label}>E-Mail</span>
          <input
            type="email"
            className={styles.input}
            value={ref?.email ?? ""}
            onChange={(e) => patch({ email: e.target.value })}
          />
        </div>
        <div className={styles.field}>
          <span className={styles.label}>Telefon</span>
          <TelefonEingabe
            className={styles.input}
            value={ref?.telefon ?? ""}
            onChange={(v) => patch({ telefon: v })}
          />
        </div>
      </div>
    </div>
  );
};

export default KontaktFeld;
